import React, { useCallback, useEffect, useState } from 'react'
import { Alert, Button, Checkbox, Form, Image, Input, Typography, Upload, message } from 'antd'
import type { UploadProps } from 'antd'
import { UploadOutlined } from '@ant-design/icons'
import { get, patch, post } from '../../lib/request'
import { errMsg } from '../../lib/api'

const { Text } = Typography
const { TextArea } = Input

/**
 * 被推荐人礼遇内容编辑（benefit_editable_v1，2026-09-14）。
 *
 * 背景：原独立管理页 /referred-benefits 在「并入答谢方案」改版时被删（App.tsx 重定向到
 * /benefit-config，AdminLayout 菜单移除），数据仍在 referred_benefits 表但后台无编辑入口。
 * 本组件把编辑能力恢复为 BenefitConfig 的第 4 个 tab（挂在「业态」后面）。
 *
 * 后端契约（referred-benefits.ts，纯复用零改动）：
 * - GET /admin/referred-benefits：全量数组（含所有状态，sortOrder 升序）；
 *   响应行字段 { id, title, titleEn, coverUrl, posterImages, sections, sortOrder, status, updatedAt }。
 *   小程序两端（首页 flattenBenefitLines / 详情页 normalizeSections）均取 status=published 的 list[0]，
 *   故本组件也只编辑 list[0]（同源）。
 * - PATCH /:id：{ title, titleEn, sections, posterImages, showOnHome }（白名单字段，status 也可 PATCH）。
 * - POST /：无记录时新建，直接传 status: 'published'（后端校验放行，无需再调 /:id/status）。
 * - sections 元素形如 { title, body, title_en?, body_en?, images? }：本表单只编辑 sections[0]
 *   的 body/body_en（小字介绍），title 固定为空串，其余 sections 原样保留不丢。
 * - 长图上传复用通用端点 POST /admin/uploads/image（multipart 字段名 file，返回 { url }）。
 *
 * 权限：路由已限 super_admin/admin（App.tsx RoleRoute）；写操作 gate = canEdit
 * （父级传入 hasPermission(role, 'BUSINESS_CONFIG_EDIT')）。后端写操作另要 CONTENT_EDIT，双保险。
 */

interface ReferredBenefitRow {
  id: number
  title: string
  titleEn: string | null
  coverUrl: string | null
  posterImages: string[] | null
  showOnHome: boolean
  // benefit_detail_entry_toggle：首页礼遇卡是否可点进详情页
  showDetailEntry: boolean
  sections: Array<{ title: string; body: string; title_en?: string; body_en?: string; images?: string[] }> | null
  sortOrder: number
  status: 'draft' | 'published' | 'offline'
  updatedAt: string
}

interface Props {
  canEdit: boolean
  /** 保存成功后通知父级（本页无联动数据，保留接口与其它 tab 一致） */
  onChanged?: () => void
}

const BenefitContentTab: React.FC<Props> = ({ canEdit, onChanged }) => {
  const [form] = Form.useForm()
  const [record, setRecord] = useState<ReferredBenefitRow | null>(null)
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [posterUrl, setPosterUrl] = useState<string>('')

  const fetchRecord = useCallback(() => {
    setLoading(true)
    get<ReferredBenefitRow[]>('/admin/referred-benefits')
      .then((d) => {
        const list = Array.isArray(d) ? d : []
        const first = list[0] ?? null
        setRecord(first)
        const sec0 = Array.isArray(first?.sections) ? first!.sections![0] : null
        form.setFieldsValue({
          title: first?.title ?? '',
          titleEn: first?.titleEn ?? '',
          body: typeof sec0?.body === 'string' ? sec0.body : '',
          bodyEn: typeof sec0?.body_en === 'string' ? sec0.body_en : '',
          // 缺省/历史记录视为 true（=现状：有图必显），与 DB 列默认值对齐
          showOnHome: first?.showOnHome ?? true,
          // benefit_detail_entry_toggle：缺省/历史记录视为 true（=现状可点进详情页）
          showDetailEntry: first?.showDetailEntry ?? true,
        })
        const posters = Array.isArray(first?.posterImages) ? first!.posterImages! : []
        setPosterUrl(typeof posters[0] === 'string' ? posters[0] : '')
      })
      .catch((err) => message.warning(errMsg(err, '礼遇内容加载失败')))
      .finally(() => setLoading(false))
  }, [form])

  useEffect(() => { fetchRecord() }, [fetchRecord])

  /** 长图上传：复用通用端点（同 ProjectIntro::customRequestLongImage），成功后本地预览 */
  const customRequestPoster: NonNullable<UploadProps['customRequest']> = async (options) => {
    const { file, onSuccess, onError } = options
    const formData = new FormData()
    formData.append('file', file as File)
    try {
      const data = await post<{ url: string }>('/admin/uploads/image', formData, {
        headers: { 'Content-Type': undefined },
      })
      if (!data?.url) throw new Error('上传返回缺少 url')
      setPosterUrl(data.url)
      onSuccess?.({ data })
      message.success('长图已上传')
    } catch (err: unknown) {
      message.error(errMsg(err, '上传失败'))
      onError?.(err instanceof Error ? err : new Error('上传失败'))
    }
  }

  const save = async () => {
    const v = await form.validateFields()
    const title = String(v.title ?? '').trim()
    if (!title) { message.warning('请填写中文标题'); return }
    // sections[0] 承载小字介绍（中/英）；既有其余 sections 原样保留，避免误删历史内容
    const prevSections = Array.isArray(record?.sections) ? record!.sections! : []
    const sec0prev = (prevSections[0] ?? {}) as Record<string, unknown>
    const sec0 = {
      ...sec0prev,
      title: typeof sec0prev.title === 'string' ? sec0prev.title : '',
      body: String(v.body ?? ''),
      body_en: String(v.bodyEn ?? ''),
    }
    const sections = [sec0, ...prevSections.slice(1)]
    const posterImages = posterUrl ? [posterUrl] : []
    const payload = {
      title,
      titleEn: String(v.titleEn ?? '').trim() || null,
      sections,
      posterImages,
      showOnHome: !!v.showOnHome,
      showDetailEntry: !!v.showDetailEntry,
    }
    setSaving(true)
    try {
      if (record?.id) {
        await patch(`/admin/referred-benefits/${record.id}`, payload)
      } else {
        // 无记录：新建并直接发布（小程序端仅读 status=published）
        await post('/admin/referred-benefits', { ...payload, status: 'published' })
      }
      message.success('礼遇内容已保存，小程序首页与详情页即时生效')
      fetchRecord()
      onChanged?.()
    } catch (err) {
      message.error(errMsg(err, '保存失败'))
    } finally {
      setSaving(false)
    }
  }

  return (
    <div style={{ maxWidth: 720 }}>
      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 16 }}
        message="此处内容发布后，在小程序首页「被推荐人礼遇」卡片及点进后的详情页展示（中文/英文随小程序语言切换）。"
      />
      {!record && !loading && (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 16 }}
          message="当前还没有礼遇内容记录，点击「保存」将新建一条并直接发布。"
        />
      )}
      <Form form={form} layout="vertical" disabled={!canEdit || loading}>
        <Form.Item
          name="title"
          label="标题（中文）"
          rules={[{ required: true, message: '请填写中文标题' }, { max: 100, message: '不超过 100 字' }]}
        >
          <Input placeholder="如：推荐好友入驻，有答谢" maxLength={100} showCount />
        </Form.Item>
        <Form.Item
          name="titleEn"
          label="标题（英文 · 小程序英文模式显示，选填）"
          rules={[{ max: 100, message: '不超过 100 字' }]}
        >
          <Input placeholder="e.g. Refer a friend and get rewarded" maxLength={100} showCount />
        </Form.Item>
        <Form.Item name="body" label="小字介绍（中文 · 支持换行）">
          <TextArea rows={4} placeholder="如：好友享入驻礼遇" />
        </Form.Item>
        <Form.Item name="bodyEn" label="小字介绍（英文 · 小程序英文模式显示，选填）">
          <TextArea rows={4} placeholder="e.g. Your friend enjoys exclusive move-in benefits" />
        </Form.Item>
        <Form.Item label="长图（选填 · 详情页全宽铺满展示；支持 JPG / PNG，单张 ≤ 10MB）">
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
            <Upload
              showUploadList={false}
              accept="image/jpeg,image/png"
              customRequest={customRequestPoster}
              disabled={!canEdit}
            >
              <Button icon={<UploadOutlined />} disabled={!canEdit}>上传长图</Button>
            </Upload>
            {posterUrl && (
              <Image
                src={posterUrl}
                alt="长图预览"
                style={{ maxWidth: 200, maxHeight: 320, borderRadius: 8, border: '1px solid #E8ECF0' }}
              />
            )}
          </div>
          {posterUrl && (
            <Text type="secondary" style={{ display: 'block', marginTop: 6, fontSize: 12, wordBreak: 'break-all' }}>
              {posterUrl}
            </Text>
          )}
        </Form.Item>
        <Form.Item name="showOnHome" valuePropName="checked" label="是否在首页展示">
          <Checkbox disabled={!posterUrl || !canEdit}>
            勾选后首页礼遇卡展示长图，点进详情页为同一张图；不勾选则首页只展示标题和简介
          </Checkbox>
          {!posterUrl && (
            <Text type="secondary" style={{ display: 'block', marginTop: 4, fontSize: 12 }}>
              请先上传长图
            </Text>
          )}
        </Form.Item>
        <Form.Item name="showDetailEntry" valuePropName="checked" label="小程序首页可点开详情页">
          <Checkbox disabled={!canEdit}>
            勾选后首页礼遇卡可点进「被推荐人礼遇」详情页；不勾选则首页只展示标题和简介，点不进去
          </Checkbox>
        </Form.Item>
        <Form.Item>
          <Button type="primary" onClick={save} loading={saving} disabled={!canEdit || loading}>
            保存
          </Button>
          {record?.updatedAt && (
            <Text type="secondary" style={{ marginLeft: 12, fontSize: 12 }}>
              最近更新：{new Date(record.updatedAt).toLocaleString()}
            </Text>
          )}
        </Form.Item>
      </Form>
    </div>
  )
}

export default BenefitContentTab
